import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { downloadMemberCard } from '../utils/memberCardGenerator';
import MemberLayout from '../components/MemberLayout';
import './Dashboard.scss';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDownloadCard = () => {
    downloadMemberCard(user);
  };

  const stats = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
        </svg>
      ),
      title: "Programmes suivis",
      value: "0",
      color: "#FF6B00",
      tooltip: "Nombre total de programmes BE TALENTED auxquels vous êtes actuellement inscrit(e)."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      ),
      title: "Formations complétées",
      value: "0",
      color: "#3B5BA5",
      tooltip: "Nombre de modules ou sessions de formation que vous avez entièrement terminés et validés."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="8" r="7"/>
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
        </svg>
      ),
      title: "Certificats obtenus",
      value: "0",
      color: "#E8731A",
      tooltip: "Nombre de certificats officiels délivrés par la Fondation BE TALENTED suite à vos formations réussies."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: "Mentorats actifs",
      value: "0",
      color: "#D63070",
      tooltip: "Nombre de sessions de mentorat individuelles ou collectives en cours avec des mentors BE TALENTED."
    }
  ];

  const profileFields = ['nom', 'prenom', 'email', 'telephone', 'universite', 'filiere', 'niveau', 'photo', 'date_naissance'];
  const filled = profileFields.filter(f => user?.[f]).length;
  const completionPct = Math.round((filled / profileFields.length) * 100);

  return (
    <MemberLayout>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Bienvenue, {user?.prenom} !</h1>
          <p>Voici un aperçu de votre activité</p>
        </div>

        <div className="dashboard-grid">
          <div className="main-content">
            <section className="member-card-section dashboard-card">
              <h3>Ma Carte de Membre</h3>
              <div className="member-card-preview">
                <div className="card-front">
                  <div className="card-header-inner">
                    <img src="/assets/IMG/logo_transparent.png" alt="BE TALENTED" className="card-logo" />
                    <h4>CARTE DE MEMBRE</h4>
                  </div>
                  <div className="card-body">
                    <div className="member-photo">
                      {user?.photo
                        ? <img src={user.photo} alt={`${user.prenom} ${user.nom}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        : <div className="photo-placeholder">{user?.prenom?.charAt(0)}{user?.nom?.charAt(0)}</div>
                      }
                    </div>
                    <div className="member-info">
                      <h4>{user?.prenom} {user?.nom}</h4>
                      <p className="member-id">ID: BT-{Date.now().toString().slice(-6)}</p>
                      <p className="member-university">{user?.universite}</p>
                      <p className="member-field">{user?.filiere} - {user?.niveau}</p>
                    </div>
                  </div>
                  <div className="card-footer-inner">
                    <p>Membre depuis {new Date().getFullYear()}</p>
                  </div>
                </div>
              </div>
              <button onClick={handleDownloadCard} className="btn-download-card">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Télécharger ma carte
              </button>
            </section>

            <section className="profile-card dashboard-card">
              <div className="profile-header">
                <div className="profile-avatar">
                  {user?.photo
                    ? <img src={user.photo} alt={`${user.prenom} ${user.nom}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : <>{user?.prenom?.charAt(0)}{user?.nom?.charAt(0)}</>
                  }
                </div>
                <div className="profile-info">
                  <h2>{user?.prenom} {user?.nom}</h2>
                  <p className="profile-meta">{user?.universite}</p>
                  <p className="profile-meta">{user?.filiere} - {user?.niveau}</p>
                </div>
                <button onClick={handleLogout} className="btn-logout">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Déconnexion
                </button>
              </div>
            </section>

            <section className="stats-section">
              <h3>Mes Statistiques</h3>
              <div className="stats-grid">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-card" style={{ '--stat-color': stat.color }} title={stat.tooltip}>
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-content">
                      <p className="stat-title">
                        {stat.title}
                        <span className="stat-info-icon" aria-label={stat.tooltip}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          <span className="stat-tooltip">{stat.tooltip}</span>
                        </span>
                      </p>
                      <p className="stat-value">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="activities-section">
              <h3>Activités Récentes</h3>
              <div className="activities-list">
                <p style={{ color: '#888', textAlign: 'center', padding: '2rem 0', fontSize: '15px' }}>
                  Aucune activité récente pour le moment.
                </p>
              </div>
            </section>
          </div>

          <aside className="sidebar">
            <section className="quick-actions">
              <h3>Actions Rapides</h3>
              <div className="actions-list">
                <Link to="/programmes" className="action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                  Explorer les programmes
                </Link>
                <Link to="/contact" className="action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  Contacter un mentor
                </Link>
                <Link to="/actualites" className="action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="9" y1="9" x2="15" y2="9"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  Voir les actualités
                </Link>
              </div>
            </section>

            <section className="upcoming-events">
              <h3>Événements à venir</h3>
              <div className="events-list">
                <p style={{ color: '#888', textAlign: 'center', padding: '1.5rem 0', fontSize: '15px' }}>
                  Aucun événement programmé.
                </p>
              </div>
            </section>

            <section className="profile-completion">
              <h3>Profil</h3>
              <div className="completion-bar">
                <div className="completion-progress" style={{ width: `${completionPct}%` }}></div>
              </div>
              <p className="completion-text">Profil complété à {completionPct}%</p>
              <Link to="/member/profil" className="btn-complete-profile">
                Compléter mon profil
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </MemberLayout>
  );
};

export default Dashboard;
